import { useMemo, useState } from 'react';
import PreferEarlierTick from '@/components/booking/PreferEarlierTick';
import { addHours, format } from 'date-fns';
import {
  ArrowRight,
  CalendarClock,
  CalendarPlus,
  CheckCircle2,
  ChevronLeft,
  Sparkles,
  Users,
  XCircle,
} from 'lucide-react';
import clsx from 'clsx';
import { aud, hoursLabel } from '@/utils/format';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { prefillOutcomeFromIntent } from '@/domain/sessionFlow';
import { generateHealingSlots } from '@/utils/rebookWindow';
import { filterFreeSlots, isArtistOffOn } from '@atink/domain';
import type {
  Artist,
  ArtistAvailability,
  Booking,
  CustomerRecord,
  DayOfWeek,
  Deposit,
  RebookAttribution,
  RebookOutcome,
  Studio,
} from '@/domain/types';

type OutcomeKind = RebookOutcome['kind'];

/** Selection marker for the calendar-picked custom slot (resolved via fitCustomSlot). */
const CUSTOM_PICK = 'custom';
const DOW: DayOfWeek[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const CUSTOM_STEP_MIN = 30;

function minutesOf(hhmm: string): number {
  const [h, m] = hhmm.split(':').map((x) => parseInt(x, 10));
  return (h ?? 0) * 60 + (m ?? 0);
}

/**
 * The custom-date pick made to fit its day (W4 follow-up): the calendar returns
 * the clicked cell (e.g. 09:00), which for a long session can start before the
 * studio opens or run across the artist's other bookings. Clip the start to
 * opening (studio hours, narrowed by the artist's rostered window), then walk
 * forward in 30-minute steps to the first start whose whole session ends by
 * close and passes filterFreeSlots (the DB double-book rule). Undefined when
 * nothing fits that day.
 */
function fitCustomSlot(
  pickIso: string,
  session: { artistId: string; durationMin: number; excludeBookingId: string },
  bookings: Record<string, Booking>,
  studioHours?: Studio['operatingHours'],
  availability?: ArtistAvailability,
): string | undefined {
  // D10-02: never offer a day the artist is on leave. The server refuses it
  // (artist-on-leave), and a refused rebook blocks the whole Floor checkout.
  if (availability && isArtistOffOn(availability, pickIso)) return undefined;
  const pattern = availability?.pattern;
  const pick = new Date(pickIso);
  const dow = DOW[pick.getDay()]!;
  let open = 0;
  let close = 24 * 60;
  if (studioHours && Object.keys(studioHours).length > 0) {
    const day = studioHours[dow];
    if (!day) return undefined;
    open = Math.max(open, minutesOf(day.open));
    close = Math.min(close, minutesOf(day.close));
  }
  if (pattern && Object.keys(pattern).length > 0) {
    const win = pattern[dow];
    if (!win) return undefined;
    open = Math.max(open, minutesOf(win.startTime));
    close = Math.min(close, minutesOf(win.endTime));
  }
  const pickMin = pick.getHours() * 60 + pick.getMinutes();
  for (let start = Math.max(pickMin, open); start + session.durationMin <= close; start += CUSTOM_STEP_MIN) {
    const at = new Date(pick);
    at.setHours(Math.floor(start / 60), start % 60, 0, 0);
    const startIso = at.toISOString();
    const endIso = new Date(at.getTime() + session.durationMin * 60_000).toISOString();
    const free = filterFreeSlots(
      [{ startIso, endIso, artistId: session.artistId, excludeBookingId: session.excludeBookingId }],
      bookings,
    );
    if (free.length > 0) return startIso;
  }
  return undefined;
}

/**
 * L3 (17 Jul): on a decline-path checkout the manager records how far the piece
 * is - finished, or partway with an approximate number of sessions still to go.
 * `checkOutBooking` turns partway into `estimatedTotalSessions` (a living number)
 * and finished into the existing mark-piece-complete path.
 */
export type PieceStateCapture = { finished: boolean; sessionsLeft?: number };

interface Props {
  booking: Booking;
  customer: CustomerRecord;
  artist: Artist;
  deposit?: Deposit;
  sessionPrice: number;
  onBack: () => void;
  onContinue: (capture: {
    outcome: RebookOutcome;
    attribution: RebookAttribution;
    selectedSlotIso?: string;
    pieceState?: PieceStateCapture;
    /** B7 (8xat D08-09): the client is happy to come in earlier (the new booking). */
    preferBroughtForward?: boolean;
  }) => void;
  /** Opens the calendar to pick a custom rebook date (Outcome A). */
  onPickCustomDate?: () => void;
  /** Seed the outcome on return from the calendar (forces the in-chair rebook). */
  initialOutcome?: OutcomeKind;
  /** A slot chosen on the calendar, shown selected in the picker. */
  customSlot?: { iso: string };
  /**
   * The progress step ticked "Piece is now complete" (tv-2na.4, Diagram 07): the
   * final session wraps as piece complete - deposit used up, no rebook reason.
   */
  pieceComplete?: boolean;
  /**
   * Bookings the picker must avoid (tv-2na.8.5): the live availability view merged
   * with the store. Only slots the DB double-book guard will accept are offered.
   * Defaults to the store's bookings.
   */
  busyBookings?: Record<string, Booking>;
  /** Shown above the picker when the last lock attempt lost the slot (409). */
  slotTakenNotice?: string;
  /** Slots already refused by the server this checkout — never offered again. */
  takenSlotIsos?: readonly string[];
  /** The studio's opening hours from the live view. Defaults to the store's studio. */
  studioHours?: Studio['operatingHours'];
}

export default function RebookOutcomePanel({
  booking,
  customer,
  artist,
  deposit,
  sessionPrice,
  onBack,
  onContinue,
  onPickCustomDate,
  initialOutcome,
  customSlot,
  pieceComplete = false,
  busyBookings,
  slotTakenNotice,
  takenSlotIsos,
  studioHours,
}: Props) {
  // Final session: only meaningful when a piece is linked (the checkbox is too).
  const finalSession = pieceComplete && !!booking.pieceId;
  // Outcome seeds from an explicit initialOutcome (calendar return) first, else
  // from the artist's in-chair read. The manager can always override. The dead
  // C-kind (defer to sales) maps to "will rebook later", which now carries the
  // deferral (L3).
  const [outcome, setOutcome] = useState<OutcomeKind | null>(() => {
    if (finalSession) return 'pieceComplete';
    const seed = initialOutcome ?? prefillOutcomeFromIntent(booking.artistRebookIntent);
    return seed === 'rebookDeferredToSales' ? 'willRebookLater' : seed;
  });

  const [selectedSlot, setSelectedSlot] = useState<string | null>(customSlot ? CUSTOM_PICK : null);
  // Mandatory free-text reasons (L3): no chips - the manager must type why.
  const [declineReasonText, setDeclineReasonText] = useState('');
  const [laterReasonText, setLaterReasonText] = useState('');
  const [preferEarlier, setPreferEarlier] = useState(false);
  // Piece-state capture on the decline paths (only when a piece is linked).
  const [pieceStage, setPieceStage] = useState<'partway' | 'finished' | null>(null);
  const [sessionsLeftInput, setSessionsLeftInput] = useState('');
  // Default to crediting the artist. The manager picking otherwise is
  // intentional - they had to step in. Friendly default nudges fair credit.
  const [facilitatedBy, setFacilitatedBy] = useState<RebookAttribution['facilitatedBy']>('artist');
  const [otherStaffId, setOtherStaffId] = useState<string>('');

  const studios = useBrainStore((s) => s.studios);
  const artists = useBrainStore((s) => s.artists);
  const agents = useBrainStore((s) => s.agents);
  const storeBookings = useBrainStore((s) => s.bookings);
  const clockIso = useSimulationStore((s) => s.clockIso);

  const studio = studios[booking.studioId];
  const managerOnDuty = studio ? agents[studio.managerId] : undefined;
  // Suggested slots in three healing bands (2/3/4 weeks), never earlier than 2
  // weeks after the sitting — and never one that overlaps a firm booking for this
  // artist (tv-2na.8.5: the audit's picker offered a slot the write then 409'd).
  const bands = useMemo(() => {
    const taken = new Set(takenSlotIsos ?? []);
    return generateHealingSlots(booking.start, clockIso, {
      artistId: booking.artistId,
      durationMin: booking.durationMin,
      bookings: busyBookings ?? storeBookings,
      excludeBookingId: booking.id,
    }, {
      // The roster + hours come from the live (DB-authoritative) records the
      // page read — the store is a bootstrap snapshot that never learns about a
      // roster changed elsewhere, so trusting it offered Pat's Sundays
      // (tv-2na.10.8). The store is only the fallback.
      studioHours: studioHours ?? studio?.operatingHours,
      availability: artist.availability ?? artists[booking.artistId]?.availability,
    },
    ).map((band) => ({ ...band, slots: band.slots.filter((iso) => !taken.has(iso)) }));
  }, [booking.start, booking.artistId, booking.durationMin, booking.id, clockIso, busyBookings, storeBookings, takenSlotIsos, studio, studioHours, artist.availability, artists]);
  const noSlots = bands.every((band) => band.slots.length === 0);
  const customFitIso = useMemo(
    () =>
      customSlot
        ? fitCustomSlot(
            customSlot.iso,
            { artistId: booking.artistId, durationMin: booking.durationMin, excludeBookingId: booking.id },
            busyBookings ?? storeBookings,
            studioHours ?? studio?.operatingHours,
            artist.availability ?? artists[booking.artistId]?.availability,
          )
        : undefined,
    [customSlot, booking.artistId, booking.durationMin, booking.id, busyBookings, storeBookings, studioHours, studio, artist.availability, artists],
  );
  const slotIso = selectedSlot === CUSTOM_PICK ? customFitIso ?? null : selectedSlot;

  // List of "other team members at this studio today" for the picker.
  const otherStaffOptions = useMemo(() => {
    const studioArtists = Object.values(artists).filter((a) => a.studioId === booking.studioId);
    const studioAgents = Object.values(agents).filter(
      (a) => a.assignedStudios.includes(booking.studioId) || a.primaryStudio === booking.studioId,
    );
    return [
      ...studioArtists.map((a) => ({
        id: a.id,
        label: `${a.displayName || a.firstName} · artist`,
      })),
      ...studioAgents.map((a) => ({ id: a.id, label: `${a.firstName} · ${a.role}` })),
    ].filter((o) => o.id !== artist.id && o.id !== studio?.managerId);
  }, [artists, agents, booking.studioId, artist.id, studio?.managerId]);

  const depositAmount = deposit?.amountAud ?? 0;
  const amountForOutcomeB = Math.max(0, sessionPrice - depositAmount);

  // Attribution applies to the two rebook wins (lock-now + will-rebook-later);
  // decline and piece complete credit the artist.
  const effectiveFacilitatedBy: RebookAttribution['facilitatedBy'] =
    outcome === 'noRebookDepositApplied' || outcome === 'pieceComplete' ? 'artist' : facilitatedBy;
  const showsAttribution = outcome === 'rebookedInChair' || outcome === 'willRebookLater';

  // L3 piece-state gating: on a decline path with a linked piece the manager must
  // record finished, or partway with at least one session still to go.
  const hasPiece = !!booking.pieceId;
  const isDeclinePath = outcome === 'willRebookLater' || outcome === 'noRebookDepositApplied';
  const sessionsLeftNum = Number(sessionsLeftInput);
  const pieceStateValid =
    !hasPiece ||
    !isDeclinePath ||
    pieceStage === 'finished' ||
    (pieceStage === 'partway' && Number.isFinite(sessionsLeftNum) && sessionsLeftNum >= 1);

  // B8 (76vb, diagram 07 + Anas 23 Sep): a reason is mandatory only when they
  // don't rebook MID-PIECE. A one-off (no piece) or a piece recorded finished
  // needs none, and isn't a decline.
  const declineNeedsReason = hasPiece && pieceStage !== 'finished';

  const continueEnabled =
    !!outcome &&
    (outcome !== 'rebookedInChair' || !!slotIso) &&
    (outcome !== 'willRebookLater' || laterReasonText.trim().length > 0) &&
    (outcome !== 'noRebookDepositApplied' || !declineNeedsReason || declineReasonText.trim().length > 0) &&
    pieceStateValid &&
    (!showsAttribution || effectiveFacilitatedBy !== 'otherStaff' || !!otherStaffId);

  function build(): {
    outcome: RebookOutcome;
    attribution: RebookAttribution;
    selectedSlotIso?: string;
    pieceState?: PieceStateCapture;
    preferBroughtForward?: boolean;
  } | null {
    if (!outcome || !effectiveFacilitatedBy) return null;
    // The sim clock, never wall time: reporting buckets the rebook by this day.
    const capturedAt = useSimulationStore.getState().clockIso;

    let facilitatedByStaffId: string;
    if (effectiveFacilitatedBy === 'artist') facilitatedByStaffId = artist.id;
    else if (effectiveFacilitatedBy === 'floorManager') facilitatedByStaffId = studio?.managerId ?? 'unknown';
    else if (effectiveFacilitatedBy === 'salesTeam') facilitatedByStaffId = 'sales-team';
    else facilitatedByStaffId = otherStaffId;

    const attribution: RebookAttribution = {
      facilitatedBy: effectiveFacilitatedBy,
      facilitatedByStaffId,
      capturedAt,
    };
    const pieceState: PieceStateCapture | undefined =
      hasPiece && isDeclinePath
        ? {
            finished: pieceStage === 'finished',
            sessionsLeft: pieceStage === 'partway' ? sessionsLeftNum : undefined,
          }
        : undefined;

    if (outcome === 'rebookedInChair' && slotIso) {
      return {
        outcome: {
          kind: 'rebookedInChair',
          newBookingId: `bk-rebook-${booking.id}-${Date.now()}`,
          depositRolledForward: depositAmount > 0,
        },
        attribution,
        selectedSlotIso: slotIso,
        ...(preferEarlier ? { preferBroughtForward: true } : {}),
      };
    }
    if (outcome === 'willRebookLater') {
      return {
        outcome: {
          kind: 'willRebookLater',
          depositRolledForward: depositAmount > 0,
          note: laterReasonText.trim(),
        },
        attribution,
        pieceState,
      };
    }
    if (outcome === 'pieceComplete') {
      return { outcome: { kind: 'pieceComplete' }, attribution, pieceState: { finished: true } };
    }
    if (outcome === 'noRebookDepositApplied') {
      return {
        outcome: {
          kind: 'noRebookDepositApplied',
          reason: declineReasonText.trim(),
        },
        attribution,
        pieceState,
      };
    }
    return null;
  }

  function handleContinue() {
    const built = build();
    if (!built) return;
    onContinue(built);
  }

  return (
    <div className="panel p-6 space-y-5" data-testid="rebook-outcome-panel">
      <header>
        <p className="label-small-caps text-gold-bright">Rebook outcome</p>
        <p className="font-serif text-cream-soft text-sm mt-1">
          {customer.firstName}&apos;s session with {artist.firstName} is wrapped. Capture what happens
          next before payment - this drives the artist + manager leaderboard.
        </p>
      </header>

      {/* Final session (Diagram 07): piece complete, deposit used up. No rebook
          reason, no piece-state question, no Ops flag. Untick on the progress
          step (Back) if the piece isn't actually finished. */}
      {finalSession ? (
        <section className="space-y-3">
          <p className="label-small-caps">What happens next?</p>
          <OutcomeButton
            active
            onClick={() => setOutcome('pieceComplete')}
            Icon={CheckCircle2}
            tone="mint"
            title="Piece complete · final session"
            subtitle={`Deposit used up · apply ${aud(depositAmount)} · charge ${aud(amountForOutcomeB)} today · no rebook needed`}
            testid="rebook-outcome-piece-complete"
          />
          <p className="font-serif text-[12px] text-cream-soft">
            Not finished after all? Go back and untick &quot;Piece is now complete&quot;.
          </p>
        </section>
      ) : (
        <section className="space-y-3">
          <p className="label-small-caps">What happens next?</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="rebook-outcome-buttons">
            <OutcomeButton
              active={outcome === 'rebookedInChair'}
              onClick={() => setOutcome('rebookedInChair')}
              Icon={CalendarPlus}
              tone="mint"
              title="Lock next session now"
              subtitle={`Roll ${aud(depositAmount)} deposit forward · pick a slot · pay ${aud(sessionPrice)} today`}
              testid="rebook-outcome-A"
            />
            <OutcomeButton
              active={outcome === 'willRebookLater'}
              onClick={() => setOutcome('willRebookLater')}
              Icon={CalendarClock}
              tone="mint"
              title="Will rebook - book later"
              subtitle={`Roll ${aud(depositAmount)} deposit forward to hold · no slot yet · pay ${aud(sessionPrice)} today`}
              testid="rebook-outcome-later"
            />
            <OutcomeButton
              active={outcome === 'noRebookDepositApplied'}
              onClick={() => setOutcome('noRebookDepositApplied')}
              Icon={XCircle}
              tone="blood"
              title="No future booking"
              subtitle={`Apply ${aud(depositAmount)} deposit · charge ${aud(amountForOutcomeB)} today`}
              testid="rebook-outcome-B"
            />
          </div>
        </section>
      )}

      {/* Outcome A: healing-banded slot picker + custom date */}
      {outcome === 'rebookedInChair' ? (
        <section className="space-y-3" data-testid="rebook-slot-picker">
          <p className="label-small-caps">Pick next slot with {artist.firstName}</p>
          {slotTakenNotice ? (
            <div
              className="rounded-md border border-blood/50 bg-blood/10 px-3 py-2 flex items-start gap-2"
              data-testid="rebook-slot-taken"
              role="alert"
            >
              <XCircle size={13} className="text-blood shrink-0 mt-0.5" />
              <p className="font-serif text-[12px] text-cream">{slotTakenNotice}</p>
            </div>
          ) : null}
          <div
            className="rounded-md border border-mint/40 bg-mint/5 px-3 py-2 flex items-start gap-2"
            data-testid="rebook-healing-note"
          >
            <Sparkles size={13} className="text-mint shrink-0 mt-0.5" />
            <p className="font-serif text-[12px] text-cream-soft">
              Tattoos need ~2 weeks to heal before the next sitting, so the earliest suggestions start
              2 weeks out.
            </p>
          </div>

          {customSlot && customFitIso ? (
            <button
              type="button"
              data-testid="rebook-custom-slot"
              onClick={() => setSelectedSlot(CUSTOM_PICK)}
              className={clsx(
                'w-full rounded-md border px-3 py-2 text-left text-sm transition flex items-center gap-2',
                selectedSlot === CUSTOM_PICK
                  ? 'border-mint bg-mint/10 text-mint'
                  : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/60',
              )}
            >
              <span className="pill-mint text-[9px]">Custom</span>
              <span className="font-inter">
                {format(new Date(customFitIso), 'EEE d MMM')} ·{' '}
                {format(new Date(customFitIso), 'h:mma').toLowerCase()}
              </span>
            </button>
          ) : null}
          {customSlot && !customFitIso ? (
            <p className="font-serif text-[12px] text-blood" data-testid="rebook-custom-slot-unfit">
              {artist.firstName} has no free {hoursLabel(booking.durationMin)} window in opening hours on{' '}
              {format(new Date(customSlot.iso), 'EEE d MMM')} — pick another date.
            </p>
          ) : null}

          {noSlots && !customSlot ? (
            <p className="font-serif text-[12px] text-cream-soft" data-testid="rebook-no-rostered-slots">
              No free rostered slots in the healing window — pick a custom date.
            </p>
          ) : null}
          {noSlots ? null : bands.map((band) => (
            <div key={band.key} data-testid={`rebook-band-${band.key}`} className="space-y-1.5">
              <p className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
                {band.weeks} weeks{band.key === '2wk' ? ' · earliest - healed' : ''}
              </p>
              {band.slots.length === 0 ? (
                // 7fra: say so, rather than a bare heading over an empty grid.
                <p className="font-serif text-[12px] text-cream-faint" data-testid={`rebook-band-empty-${band.key}`}>
                  Nothing free at {band.weeks} weeks.
                </p>
              ) : null}
              <div className="grid grid-cols-2 gap-2">
                {band.slots.map((iso) => {
                  const active = selectedSlot === iso;
                  return (
                    <button
                      key={iso}
                      type="button"
                      onClick={() => setSelectedSlot(iso)}
                      data-testid={`rebook-slot-${iso}`}
                      data-slot-iso={iso}
                      className={clsx(
                        'rounded-md border px-3 py-2 text-left text-sm transition',
                        active
                          ? 'border-mint bg-mint/10 text-mint'
                          : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/60',
                      )}
                    >
                      <span className="block font-inter">{format(new Date(iso), 'EEE d MMM')}</span>
                      <span className="block font-inter text-[10px] uppercase tracking-widest text-cream-faint mt-1">
                        {format(new Date(iso), 'h:mma').toLowerCase()} ·{' '}
                        {format(addHours(new Date(iso), 4), 'h:mma').toLowerCase()}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {onPickCustomDate ? (
            <div className="pt-3 border-t border-dashed border-gold-dim/40">
              <button
                type="button"
                onClick={onPickCustomDate}
                data-testid="rebook-pick-custom"
                className="btn-ghost text-sm"
              >
                <CalendarPlus size={14} /> Pick a custom date on the calendar
              </button>
              <p className="font-serif text-[12px] text-cream-soft mt-1.5">
                Opens {artist.firstName}&apos;s calendar. Dates under 2 weeks ask you to confirm the
                healing window. You will come back here with the date filled in.
              </p>
            </div>
          ) : null}
          <PreferEarlierTick checked={preferEarlier} onChange={setPreferEarlier} className="mt-2" />
        </section>
      ) : null}

      {/* Will rebook later: deposit rolls to hold, mandatory free-text reason
          (the deferral note for the sales team) + piece-state capture. */}
      {outcome === 'willRebookLater' ? (
        <section className="space-y-2" data-testid="rebook-later-note">
          <p className="label-small-caps">
            Why not now? <span aria-hidden="true">*</span>
          </p>
          <textarea
            value={laterReasonText}
            onChange={(e) => setLaterReasonText(e.target.value)}
            placeholder="Type why they're not booking today - this goes to the sales team to follow up. Required."
            rows={2}
            className="w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream font-serif"
            data-testid="rebook-later-reason-input"
          />
          <p className="font-serif text-[12px] text-cream-soft">
            {aud(depositAmount)} deposit rolls forward as a held pot - no slot booked yet. Sales
            follows up.
          </p>
          <PieceStateCapture
            hasPiece={hasPiece}
            stage={pieceStage}
            onStage={setPieceStage}
            sessionsLeft={sessionsLeftInput}
            onSessionsLeft={setSessionsLeftInput}
          />
        </section>
      ) : null}

      {/* Outcome B: mandatory free-text reason + piece-state capture. */}
      {outcome === 'noRebookDepositApplied' ? (
        <section className="space-y-2" data-testid="rebook-decline-reason">
          <p className="label-small-caps">
            Why aren&apos;t they rebooking?{' '}
            {declineNeedsReason ? (
              <span aria-hidden="true">*</span>
            ) : (
              <span className="normal-case tracking-normal text-cream-faint" data-testid="rebook-decline-reason-optional">
                (optional{hasPiece ? '' : ' for a one-off'})
              </span>
            )}
          </p>
          <textarea
            value={declineReasonText}
            onChange={(e) => setDeclineReasonText(e.target.value)}
            placeholder={
              declineNeedsReason ? "Type why they're not rebooking mid-piece. Required." : 'Anything worth noting? Optional.'
            }
            rows={2}
            className="w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream font-serif"
            data-testid="rebook-decline-reason-input"
          />
          <PieceStateCapture
            hasPiece={hasPiece}
            stage={pieceStage}
            onStage={setPieceStage}
            sessionsLeft={sessionsLeftInput}
            onSessionsLeft={setSessionsLeftInput}
          />
        </section>
      ) : null}

      {/* Attribution selector - the two rebook wins (lock-now + will-rebook-later).
          The artist is the default so the manager has to actively switch off it
          when they stepped in. Keeps credit fair without feeling competitive. */}
      {showsAttribution ? (
        <section className="space-y-2" data-testid="rebook-attribution">
          <p className="label-small-caps flex items-center gap-1.5">
            <Users size={11} /> Credit this rebook to...
          </p>
          <p className="font-serif text-[12px] text-cream-soft">
            Default is to credit the artist. Only switch if you stepped in cold to close it.
          </p>
          <div className="flex flex-col gap-1.5">
            <AttrRadio
              label={`${artist.firstName} closed it`}
              value="artist"
              active={effectiveFacilitatedBy === 'artist'}
              disabled={false}
              onClick={() => setFacilitatedBy('artist')}
              testid="rebook-attr-artist"
              tone="mint"
            />
            <AttrRadio
              label={`${managerOnDuty?.firstName ?? 'Manager'} stepped in`}
              value="floorManager"
              active={effectiveFacilitatedBy === 'floorManager'}
              disabled={false}
              onClick={() => setFacilitatedBy('floorManager')}
              testid="rebook-attr-manager"
              tone="gold"
            />
            <AttrRadio
              label="Someone else helped"
              value="otherStaff"
              active={effectiveFacilitatedBy === 'otherStaff'}
              disabled={false}
              onClick={() => setFacilitatedBy('otherStaff')}
              testid="rebook-attr-other"
              tone="cream"
            />
            {effectiveFacilitatedBy === 'otherStaff' ? (
              <select
                value={otherStaffId}
                onChange={(e) => setOtherStaffId(e.target.value)}
                className="ml-6 mt-1 bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
                data-testid="rebook-attr-other-picker"
              >
                <option value="">Pick someone...</option>
                {otherStaffOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
            ) : null}
          </div>
        </section>
      ) : null}

      <footer className="flex justify-between pt-2 border-t border-gold-dim/15">
        <button
          type="button"
          onClick={onBack}
          className="btn-quiet text-sm"
          data-testid="rebook-back"
        >
          <ChevronLeft size={12} /> Back
        </button>
        <button
          type="button"
          onClick={handleContinue}
          disabled={!continueEnabled}
          className="btn-primary text-sm disabled:opacity-40"
          data-testid="rebook-continue"
        >
          Continue <ArrowRight size={14} />
        </button>
      </footer>
    </div>
  );
}

/**
 * L3 piece-state capture, shown on the decline paths when a piece is linked. Two
 * options - partway or finished; partway reveals an "approximate sessions left"
 * number that becomes the piece's living `estimatedTotalSessions` at checkout.
 */
function PieceStateCapture({
  hasPiece,
  stage,
  onStage,
  sessionsLeft,
  onSessionsLeft,
}: {
  hasPiece: boolean;
  stage: 'partway' | 'finished' | null;
  onStage: (s: 'partway' | 'finished') => void;
  sessionsLeft: string;
  onSessionsLeft: (v: string) => void;
}) {
  if (!hasPiece) return null;
  return (
    <div className="mt-3 pt-3 border-t border-dashed border-gold-dim/30" data-testid="rebook-piece-state">
      <p className="label-small-caps mb-1.5">
        Where&apos;s the piece up to? <span aria-hidden="true">*</span>
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onStage('partway')}
          data-testid="rebook-piece-partway"
          aria-pressed={stage === 'partway'}
          className={clsx(
            'flex-1 rounded-md border px-3 py-2 text-sm transition',
            stage === 'partway'
              ? 'border-gold-bright bg-gold/10 text-gold-bright'
              : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/50',
          )}
        >
          Partway finished
        </button>
        <button
          type="button"
          onClick={() => onStage('finished')}
          data-testid="rebook-piece-finished"
          aria-pressed={stage === 'finished'}
          className={clsx(
            'flex-1 rounded-md border px-3 py-2 text-sm transition',
            stage === 'finished'
              ? 'border-mint bg-mint/10 text-mint'
              : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/50',
          )}
        >
          Finished
        </button>
      </div>
      {stage === 'partway' ? (
        <div className="mt-2">
          <label htmlFor="rebook-sessions-left" className="label-small-caps text-cream-faint">
            Approximate sessions left
          </label>
          <input
            id="rebook-sessions-left"
            type="number"
            min={1}
            step={1}
            inputMode="numeric"
            value={sessionsLeft}
            onChange={(e) => onSessionsLeft(e.target.value)}
            placeholder="e.g. 3"
            className="mt-1 w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
            data-testid="rebook-sessions-left"
          />
          <p className="font-serif text-[12px] text-cream-soft mt-1">
            Updates the piece&apos;s total sessions estimate - it keeps refreshing each visit.
          </p>
        </div>
      ) : null}
    </div>
  );
}

function OutcomeButton({
  active,
  onClick,
  Icon,
  tone,
  title,
  subtitle,
  testid,
}: {
  active: boolean;
  onClick: () => void;
  Icon: typeof CalendarPlus;
  tone: 'mint' | 'blood' | 'gold';
  title: string;
  subtitle: string;
  testid: string;
}) {
  const toneActive =
    tone === 'mint'
      ? 'border-mint bg-mint/10 text-mint'
      : tone === 'blood'
        ? 'border-blood bg-blood/10 text-blood'
        : 'border-gold-bright bg-gold/10 text-gold-bright';
  const toneIdle =
    tone === 'mint'
      ? 'border-mint/30 text-cream-soft hover:border-mint/70'
      : tone === 'blood'
        ? 'border-blood/30 text-cream-soft hover:border-blood/70'
        : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/60';

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={clsx(
        'rounded-lg border p-4 text-left transition flex flex-col gap-2',
        active ? toneActive : toneIdle,
      )}
    >
      <span className="flex items-center gap-2">
        <Icon size={14} />
        <span className="font-inter text-sm">{title}</span>
      </span>
      <span className="font-serif text-xs leading-relaxed">{subtitle}</span>
    </button>
  );
}

function AttrRadio({
  label,
  active,
  disabled,
  onClick,
  testid,
  tone = 'gold',
}: {
  label: string;
  value: string;
  active: boolean;
  disabled: boolean;
  onClick: () => void;
  testid: string;
  tone?: 'mint' | 'gold' | 'cream';
}) {
  const activeClasses =
    tone === 'mint'
      ? 'border-mint bg-mint/10 text-mint'
      : tone === 'cream'
        ? 'border-cream-soft/60 bg-cream-soft/10 text-cream'
        : 'border-gold-bright bg-gold/10 text-gold-bright';
  const dotClasses =
    tone === 'mint'
      ? 'bg-mint border-mint'
      : tone === 'cream'
        ? 'bg-cream-soft border-cream-soft'
        : 'bg-gold-bright border-gold-bright';
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      data-testid={testid}
      className={clsx(
        'flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm transition text-left',
        active
          ? activeClasses
          : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/50',
        disabled ? 'opacity-40 cursor-not-allowed' : '',
      )}
    >
      <span
        className={clsx(
          'inline-block h-2.5 w-2.5 rounded-full border',
          active ? dotClasses : 'border-gold-dim/60',
        )}
      />
      <span>{label}</span>
      <span className="hidden">
        <Sparkles size={1} />
      </span>
    </button>
  );
}
